import { useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

const PANEL_PARAM = 'patient';

interface OpenedFromListState {
    messagePanelFromList: true;
}

const OPENED_FROM_LIST: OpenedFromListState = { messagePanelFromList: true };

function isOpenedFromList(state: unknown): boolean {
    return (
        typeof state === 'object' &&
        state !== null &&
        (state as Partial<OpenedFromListState>).messagePanelFromList === true
    );
}

function withPanelParam(prev: URLSearchParams, patientId: string | null): URLSearchParams {
    const next = new URLSearchParams(prev);
    if (patientId) next.set(PANEL_PARAM, patientId);
    else next.delete(PANEL_PARAM);
    return next;
}

// The open panel lives in ?patient=, so it deep-links; opening pushes history so Back closes it.
export function useMessagePanelSelection() {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const selectedPatientId = searchParams.get(PANEL_PARAM)?.trim() || null;
    const openedFromList = isOpenedFromList(location.state);

    const openPatient = useCallback(
        (patientId: string) => {
            if (patientId === selectedPatientId) return;
            setSearchParams((prev) => withPanelParam(prev, patientId), {
                state: OPENED_FROM_LIST
            });
        },
        [selectedPatientId, setSearchParams]
    );

    // A deep link has no list entry behind it to pop back to, so it drops the param in place.
    const closePanel = useCallback(() => {
        if (openedFromList) {
            navigate(-1);
            return;
        }
        setSearchParams((prev) => withPanelParam(prev, null), { replace: true });
    }, [openedFromList, navigate, setSearchParams]);

    return { selectedPatientId, openPatient, closePanel };
}
